'use client';

import { useSearchParams } from 'next/navigation';

import { BAZAK_URL, EMBED_CONTEXT } from '@/common/constants';
import { useTenantKey } from '@/components/providers/config-provider';
import { useReadOnly } from '@/components/providers/read-only-provider';
import { watermarkUrl, type WatermarkMedium } from '@/lib/utils/watermark-url';

/**
 * Read from the URL the widget already builds (`/embed?displayMode=…`) rather than
 * from config, so it reflects what is actually on screen. `useSearchParams` and not
 * `window.location`, so the value matches on the server and the first client render —
 * the href would otherwise mismatch during hydration.
 */
function useWatermarkMedium(): WatermarkMedium {
  const searchParams = useSearchParams();

  if (searchParams.get('context') === EMBED_CONTEXT.INLINE) return 'inline';

  const displayMode = searchParams.get('displayMode');
  if (displayMode === 'copilot') return 'copilot';
  if (displayMode === 'bubble') return 'bubble';
  // No displayMode means nobody embedded us — a direct visit to {tenant}.bazak.ai.
  return 'standalone';
}

export function useWatermarkUrl(): string {
  const medium = useWatermarkMedium();
  const tenantKey = useTenantKey();
  // The back-office replays conversations through this same component, and tagging
  // those would report our own clicks as a customer site's traffic.
  const isReadOnly = useReadOnly();

  if (isReadOnly) return BAZAK_URL;
  return watermarkUrl({ medium, tenantKey });
}
